import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import PublicOffIcon from '@mui/icons-material/PublicOff'
import SaveIcon from '@mui/icons-material/Save'
import SettingsIcon from '@mui/icons-material/Settings'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import httpClient from 'src/utilities/http-client'
import { BlockBuilder } from 'src/v3/components/show-notes-editor/block-builder'
import {
  assignContributorToChapter,
  chapterContributorsCleared,
  chapterPublishStatusCleared,
  deleteChapter,
  fetchBookChapters,
  fetchChapterContributors,
  fetchContributors,
  publishChapter,
  removeContributorFromChapter,
  selectChapterContributors,
  selectChapterContributorsStatus,
  selectChapters,
  selectContributors,
  selectLastPublishedChapter,
  selectPublishChapterStatus,
  selectUnpublishChapterStatus,
  unpublishChapter,
  updateChapter,
} from '../../../codex.slice'

let ChapterEditor = (): ReactElement => {
  let [showNotesQueryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: { retry: 1 },
        },
      }),
  )
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { bookID, chapterID } = useParams<{ bookID: string; chapterID: string }>()

  let chapters = useAppSelector(selectChapters)
  let chapter = chapters.find((c) => c.id === chapterID)
  let allContributors = useAppSelector(selectContributors)
  let chapterContributors = useAppSelector(selectChapterContributors)
  let chapterContributorsStatus = useAppSelector(
    selectChapterContributorsStatus,
  )
  let publishStatus = useAppSelector(selectPublishChapterStatus)
  let lastPublished = useAppSelector(selectLastPublishedChapter)
  let unpublishStatus = useAppSelector(selectUnpublishChapterStatus)

  let [settingsOpen, setSettingsOpen] = useState(false)
  let [title, setTitle] = useState('')
  let [subtitle, setSubtitle] = useState('')
  let [hasChanges, setHasChanges] = useState(false)
  let [showNotesID, setShowNotesID] = useState<string | null>(null)
  let [isCreatingShowNotes, setIsCreatingShowNotes] = useState(false)
  let [selectedContributorID, setSelectedContributorID] = useState('')
  let [isAssigning, setIsAssigning] = useState(false)

  useEffect(() => {
    if (bookID) {
      dispatch(fetchBookChapters(bookID))
    }
  }, [bookID, dispatch])

  useEffect(() => {
    if (chapter) {
      setTitle(chapter.title)
      setSubtitle(chapter.subtitle || '')
      setHasChanges(false)
    }
  }, [chapter])

  useEffect(() => {
    if (!chapterID) return

    let checkAndCreateShowNotes = async () => {
      try {
        setIsCreatingShowNotes(true)
        let getResponse = await httpClient.get(
          `/api/codex/chapters/${chapterID}/show-notes`,
        )
        if (Array.isArray(getResponse.data) && getResponse.data.length === 0) {
          let createResponse = await httpClient.post(
            `/api/codex/chapters/${chapterID}/show-notes`,
          )
          setShowNotesID(createResponse.data.id)
        } else {
          setShowNotesID(getResponse.data[0]?.id || null)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize show notes:', error)
      } finally {
        setIsCreatingShowNotes(false)
      }
    }

    checkAndCreateShowNotes()
  }, [chapterID])

  let handleSettingsOpen = () => {
    setSettingsOpen(true)
    if (chapterID) {
      dispatch(fetchChapterContributors(chapterID))
    }
    if (allContributors.length === 0) {
      dispatch(fetchContributors())
    }
  }

  let handleSettingsClose = () => {
    setSettingsOpen(false)
    dispatch(chapterContributorsCleared())
    dispatch(chapterPublishStatusCleared())
    setSelectedContributorID('')
  }

  let handleSave = () => {
    if (!chapterID) return
    dispatch(
      updateChapter({
        id: chapterID,
        data: {
          title,
          subtitle: subtitle || undefined,
        },
      }),
    )
    setHasChanges(false)
  }

  let handleAssignContributor = async () => {
    if (!chapterID || !selectedContributorID) return
    setIsAssigning(true)
    // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
    await dispatch(
      assignContributorToChapter({
        chapterID,
        data: { contributorID: selectedContributorID },
      }),
    )
    setSelectedContributorID('')
    setIsAssigning(false)
  }

  let handleRemoveContributor = (contributorID: string) => {
    if (!chapterID) return
    dispatch(removeContributorFromChapter({ chapterID, contributorID }))
  }

  let assignedIDs = new Set(chapterContributors.map((c) => c.id))
  let availableContributors = allContributors.filter(
    (c) => !assignedIDs.has(c.id),
  )

  return (
    <>
      <Container maxWidth="xl" className="py-6">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/codex/books/${bookID}`)}
          >
            Back to Book
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              {hasChanges ? 'Save' : 'Saved'}
            </Button>
            <IconButton onClick={handleSettingsOpen}>
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        {!chapter && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {chapter && (
          <Box>
            <TextField
              fullWidth
              variant="standard"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                setHasChanges(true)
              }}
              placeholder="Chapter Title"
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    '& input': { padding: 0 },
                  },
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              variant="standard"
              value={subtitle}
              onChange={(e) => {
                setSubtitle(e.target.value)
                setHasChanges(true)
              }}
              placeholder="Chapter subtitle..."
              sx={{ mb: 4 }}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: '1.1rem',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    '& textarea': { padding: 0 },
                  },
                },
              }}
            />

            {showNotesID && (
              <QueryClientProvider client={showNotesQueryClient}>
                <BlockBuilder
                  showNotesID={showNotesID}
                  basePath="codex/chapters/show-notes"
                  showNotesName={title}
                  showNotesDescription={subtitle}
                />
              </QueryClientProvider>
            )}

            {isCreatingShowNotes && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Container>
      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={handleSettingsClose}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              p: 3,
              bgcolor: 'background.default',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6">Chapter Settings</Typography>
            <IconButton onClick={handleSettingsClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Contributors
            </Typography>

            {chapterContributorsStatus === 'pending' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {chapterContributors.length > 0 && (
              <List disablePadding sx={{ mb: 1.5 }}>
                {chapterContributors.map((contributor) => (
                  <ListItem
                    key={contributor.id}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveContributor(contributor.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        src={
                          contributor.profileImage
                            ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${contributor.profileImage}`
                            : undefined
                        }
                        sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                      >
                        {contributor.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={contributor.name}
                      secondary={
                        [contributor.credentials, contributor.title]
                          .filter(Boolean)
                          .join(' · ') || undefined
                      }
                      slotProps={{
                        primary: { variant: 'body2' },
                        secondary: { variant: 'caption' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {availableContributors.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Select
                  size="small"
                  displayEmpty
                  value={selectedContributorID}
                  onChange={(e) => setSelectedContributorID(e.target.value)}
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="" disabled>
                    <Typography variant="body2" color="text.disabled">
                      Add contributor...
                    </Typography>
                  </MenuItem>
                  {availableContributors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Avatar
                          src={
                            c.profileImage
                              ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${c.profileImage}`
                              : undefined
                          }
                          sx={{ width: 22, height: 22, fontSize: '0.65rem' }}
                        >
                          {c.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{c.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAssignContributor}
                  disabled={!selectedContributorID || isAssigning}
                >
                  {isAssigning ? <CircularProgress size={16} /> : 'Add'}
                </Button>
              </Box>
            )}

            {availableContributors.length === 0 &&
              chapterContributorsStatus === 'fulfilled' &&
              allContributors.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  All contributors assigned.
                </Typography>
              )}

            {allContributors.length === 0 &&
              chapterContributorsStatus !== 'pending' && (
                <Typography variant="caption" color="text.secondary">
                  No contributors yet.{' '}
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => navigate('/codex/contributors')}
                  >
                    Create one first.
                  </Link>
                </Typography>
              )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Publish
            </Typography>

            {publishStatus === 'fulfilled' &&
              lastPublished &&
              unpublishStatus !== 'pending' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    color: 'success.main',
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                  <Typography variant="caption">
                    Published as version {lastPublished.version} &middot;{' '}
                    {new Date(lastPublished.publishedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}

            {publishStatus === 'rejected' && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mb: 1.5 }}
              >
                Publish failed — please try again.
              </Typography>
            )}

            {unpublishStatus === 'fulfilled' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  color: 'text.secondary',
                }}
              >
                <PublicOffIcon fontSize="small" />
                <Typography variant="caption">
                  Unpublished — chapter removed from Codex
                </Typography>
              </Box>
            )}

            {unpublishStatus === 'rejected' && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mb: 1.5 }}
              >
                Unpublish failed — please try again.
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={
                  publishStatus === 'pending' ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                onClick={() => {
                  if (chapterID) dispatch(publishChapter(chapterID))
                }}
                disabled={
                  publishStatus === 'pending' || unpublishStatus === 'pending'
                }
                size="small"
                sx={{ flex: 1 }}
              >
                Publish
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  unpublishStatus === 'pending' ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PublicOffIcon />
                  )
                }
                onClick={() => {
                  if (chapterID) dispatch(unpublishChapter(chapterID))
                }}
                disabled={
                  publishStatus === 'pending' || unpublishStatus === 'pending'
                }
                size="small"
                sx={{ flex: 1 }}
              >
                Unpublish
              </Button>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              if (!chapterID) return
              dispatch(deleteChapter(chapterID))
              navigate(`/codex/books/${bookID}`)
            }}
            fullWidth
          >
            Delete Chapter
          </Button>
        </Box>
      </Drawer>
    </>
  )
}

export default ChapterEditor
